import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Plus,
  Trash2,
  X,
  GripVertical,
  ChevronDown,
} from "lucide-react";

// Inline editable cell
const EditableCell = ({ getValue, row, column, table }) => {
  const initialValue = getValue();
  const [value, setValue] = useState(initialValue ?? "");
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const meta = table.options.meta;
  const isReadOnly = meta?.readOnly;
  const columnId = column.id;

  useEffect(() => {
    setValue(initialValue ?? "");
  }, [initialValue]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const onBlur = () => {
    setEditing(false);
    if (value !== initialValue) {
      meta?.updateCell(row.index, columnId, value);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      onBlur();
    }
    if (e.key === "Escape") {
      setValue(initialValue ?? "");
      setEditing(false);
    }
    if (e.key === "Tab") {
      onBlur();
    }
  };

  if (isReadOnly || columnId === "actions") {
    return <span className="text-sm">{value}</span>;
  }

  if (editing) {
    return (
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className="h-7 text-xs px-2 py-1 border-blue-400 focus:ring-1 focus:ring-blue-400"
        data-testid={`edit-cell-${row.index}-${columnId}`}
      />
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      className="cursor-pointer min-h-[28px] flex items-center px-1 rounded hover:bg-blue-50 hover:ring-1 hover:ring-blue-200 transition-all text-sm"
      title="Click to edit"
      data-testid={`cell-${row.index}-${columnId}`}
    >
      {value || <span className="text-slate-300 italic text-xs">Click to edit</span>}
    </div>
  );
};

// Predecessor select cell
const PredecessorCell = ({ getValue, row, table }) => {
  const initialValue = getValue() || "";
  const [editing, setEditing] = useState(false);
  const meta = table.options.meta;
  const isReadOnly = meta?.readOnly;
  const allTasks = meta?.tasks || [];

  if (isReadOnly) {
    return <span className="text-sm font-mono text-slate-600">{initialValue || "—"}</span>;
  }

  if (editing) {
    return (
      <Select
        value={initialValue || "none"}
        onValueChange={(val) => {
          meta?.updateCell(row.index, "predecessor_task_id", val === "none" ? "" : val);
          setEditing(false);
        }}
      >
        <SelectTrigger className="h-7 text-xs" data-testid={`pred-select-${row.index}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">None (Start)</SelectItem>
          {allTasks
            .filter((_, i) => i !== row.index)
            .map((t) => (
              <SelectItem key={t.task_id} value={t.task_id}>
                {t.task_id}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      className="cursor-pointer font-mono text-slate-600 text-sm min-h-[28px] flex items-center px-1 rounded hover:bg-blue-50 hover:ring-1 hover:ring-blue-200"
      title="Click to change"
    >
      {initialValue || "—"}
    </div>
  );
};

// Column header with sort + filter
const ColumnHeader = ({ column, title }) => {
  const [showFilter, setShowFilter] = useState(false);
  const sorted = column.getIsSorted();
  const filterValue = column.getFilterValue() ?? "";

  return (
    <div className="space-y-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-1 text-white font-semibold text-xs hover:text-blue-200 transition-colors w-full">
            {title}
            {sorted === "asc" ? (
              <ArrowUp className="w-3 h-3" />
            ) : sorted === "desc" ? (
              <ArrowDown className="w-3 h-3" />
            ) : (
              <ChevronDown className="w-3 h-3 opacity-50" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-40">
          <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
            <ArrowUp className="w-3.5 h-3.5 mr-2" /> Sort A-Z
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
            <ArrowDown className="w-3.5 h-3.5 mr-2" /> Sort Z-A
          </DropdownMenuItem>
          {sorted && (
            <DropdownMenuItem onClick={() => column.clearSorting()}>
              <ArrowUpDown className="w-3.5 h-3.5 mr-2" /> Clear Sort
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowFilter(!showFilter)}>
            <Filter className="w-3.5 h-3.5 mr-2" /> {showFilter ? "Hide Filter" : "Filter"}
          </DropdownMenuItem>
          {filterValue && (
            <DropdownMenuItem onClick={() => column.setFilterValue("")}>
              <X className="w-3.5 h-3.5 mr-2" /> Clear Filter
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {showFilter && (
        <Input
          value={filterValue}
          onChange={(e) => column.setFilterValue(e.target.value)}
          placeholder={`Filter...`}
          className="h-6 text-xs bg-white/10 border-white/20 text-white placeholder:text-white/40 px-1.5"
        />
      )}
    </div>
  );
};

export function RecoveryTaskTable({
  tasks = [],
  onTasksChange,
  readOnly = false,
  showAddForm = true,
  emptyMessage = "No tasks added yet.",
  userName = "",
}) {
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [showNewRow, setShowNewRow] = useState(false);
  const [newTask, setNewTask] = useState({
    task_id: `T${String(tasks.length + 1).padStart(3, "0")}`,
    actual_task: "",
    task_owner: userName,
    predecessor_task_id: "",
    estimated_duration: "30 mins",
    estimated_duration_mins: 30,
    notes: "",
  });

  const updateCell = useCallback(
    (rowIndex, columnId, value) => {
      if (!onTasksChange) return;
      const updated = tasks.map((task, i) => {
        if (i === rowIndex) {
          const newTask = { ...task, [columnId]: value };
          if (columnId === "estimated_duration") {
            const mins = parseInt(value) || 0;
            newTask.estimated_duration_mins = mins > 0 ? mins : task.estimated_duration_mins;
          }
          return newTask;
        }
        return task;
      });
      onTasksChange(updated);
    },
    [tasks, onTasksChange]
  );

  const deleteRow = useCallback(
    (rowIndex) => {
      if (!onTasksChange) return;
      onTasksChange(tasks.filter((_, i) => i !== rowIndex));
    },
    [tasks, onTasksChange]
  );

  const addTask = useCallback(() => {
    if (!newTask.actual_task.trim()) return;
    const taskId =
      newTask.task_id || `T${String(tasks.length + 1).padStart(3, "0")}`;
    const created = {
      ...newTask,
      task_id: taskId,
      start_time: null,
      completion_time: null,
      status: "not_started",
    };
    if (onTasksChange) {
      onTasksChange([...tasks, created]);
    }
    setNewTask({
      task_id: `T${String(tasks.length + 2).padStart(3, "0")}`,
      actual_task: "",
      task_owner: userName,
      predecessor_task_id: taskId,
      estimated_duration: "30 mins",
      estimated_duration_mins: 30,
      notes: "",
    });
    setShowNewRow(false);
  }, [newTask, tasks, onTasksChange, userName]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "task_id",
        header: ({ column }) => <ColumnHeader column={column} title="Task ID" />,
        cell: ({ getValue, row, column, table }) => {
          const meta = table.options.meta;
          if (meta?.readOnly) {
            return <span className="font-mono font-bold text-purple-700 text-xs">{getValue()}</span>;
          }
          return <EditableCell getValue={getValue} row={row} column={column} table={table} />;
        },
        size: 70,
      },
      {
        accessorKey: "actual_task",
        header: ({ column }) => <ColumnHeader column={column} title="Actual Task" />,
        cell: EditableCell,
        size: 250,
      },
      {
        accessorKey: "task_owner",
        header: ({ column }) => <ColumnHeader column={column} title="Task Owner" />,
        cell: EditableCell,
        size: 130,
      },
      {
        accessorKey: "predecessor_task_id",
        header: ({ column }) => <ColumnHeader column={column} title="Predecessor" />,
        cell: PredecessorCell,
        size: 100,
      },
      {
        accessorKey: "estimated_duration",
        header: ({ column }) => <ColumnHeader column={column} title="Est. Duration" />,
        cell: EditableCell,
        size: 110,
      },
      {
        accessorKey: "start_time",
        header: ({ column }) => <ColumnHeader column={column} title="Start Time" />,
        cell: ({ getValue }) => (
          <span className="text-xs text-slate-400 italic">{getValue() || "—"}</span>
        ),
        size: 90,
      },
      {
        accessorKey: "completion_time",
        header: ({ column }) => <ColumnHeader column={column} title="Completion" />,
        cell: ({ getValue }) => (
          <span className="text-xs text-slate-400 italic">{getValue() || "—"}</span>
        ),
        size: 100,
      },
      {
        accessorKey: "notes",
        header: ({ column }) => <ColumnHeader column={column} title="Notes" />,
        cell: EditableCell,
        size: 140,
      },
      ...(!readOnly
        ? [
            {
              id: "actions",
              header: () => null,
              cell: ({ row }) => (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-red-500 hover:bg-red-50"
                  onClick={() => deleteRow(row.index)}
                  data-testid={`delete-task-${row.index}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              ),
              size: 40,
            },
          ]
        : []),
    ],
    [readOnly, deleteRow]
  );

  const table = useReactTable({
    data: tasks,
    columns,
    state: { sorting, columnFilters },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    meta: {
      updateCell,
      readOnly,
      tasks,
    },
  });

  const activeFilters = columnFilters.filter((f) => f.value);

  return (
    <div className="space-y-3" data-testid="recovery-task-table">
      {/* Toolbar */}
      {activeFilters.length > 0 && (
        <div className="flex items-center gap-2 text-xs">
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          {activeFilters.map((f) => (
            <span
              key={f.id}
              className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full"
            >
              {f.id}: {f.value}
              <button
                onClick={() => table.getColumn(f.id)?.setFilterValue("")}
                className="hover:text-red-500"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button
            onClick={() => setColumnFilters([])}
            className="text-slate-500 hover:text-red-500 underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-slate-800">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    style={{ width: header.getSize() }}
                    className="px-2 py-2"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row, idx) => (
                <TableRow
                  key={row.id}
                  className={`${idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"} hover:bg-blue-50/40 transition-colors`}
                  data-testid={`task-row-${idx}`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="px-2 py-1.5" style={{ width: cell.column.getSize() }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center text-sm text-slate-400 py-8 italic"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}

            {/* Inline new row */}
            {showNewRow && !readOnly && (
              <TableRow className="bg-emerald-50/50 border-t-2 border-emerald-200">
                <TableCell className="px-2 py-1.5">
                  <Input
                    value={newTask.task_id}
                    onChange={(e) => setNewTask((p) => ({ ...p, task_id: e.target.value }))}
                    className="h-7 text-xs font-mono"
                    data-testid="new-row-task-id"
                  />
                </TableCell>
                <TableCell className="px-2 py-1.5">
                  <Input
                    value={newTask.actual_task}
                    onChange={(e) => setNewTask((p) => ({ ...p, actual_task: e.target.value }))}
                    placeholder="Describe the recovery task..."
                    className="h-7 text-xs"
                    onKeyDown={(e) => e.key === "Enter" && addTask()}
                    autoFocus
                    data-testid="new-row-actual-task"
                  />
                </TableCell>
                <TableCell className="px-2 py-1.5">
                  <Input
                    value={newTask.task_owner}
                    onChange={(e) => setNewTask((p) => ({ ...p, task_owner: e.target.value }))}
                    className="h-7 text-xs"
                    data-testid="new-row-owner"
                  />
                </TableCell>
                <TableCell className="px-2 py-1.5">
                  <Select
                    value={newTask.predecessor_task_id || "none"}
                    onValueChange={(val) =>
                      setNewTask((p) => ({ ...p, predecessor_task_id: val === "none" ? "" : val }))
                    }
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {tasks.map((t) => (
                        <SelectItem key={t.task_id} value={t.task_id}>
                          {t.task_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="px-2 py-1.5">
                  <Input
                    value={newTask.estimated_duration}
                    onChange={(e) => setNewTask((p) => ({ ...p, estimated_duration: e.target.value }))}
                    className="h-7 text-xs"
                    data-testid="new-row-duration"
                  />
                </TableCell>
                <TableCell className="px-2 py-1.5 text-xs text-slate-400 italic">—</TableCell>
                <TableCell className="px-2 py-1.5 text-xs text-slate-400 italic">—</TableCell>
                <TableCell className="px-2 py-1.5">
                  <Input
                    value={newTask.notes}
                    onChange={(e) => setNewTask((p) => ({ ...p, notes: e.target.value }))}
                    placeholder="Optional"
                    className="h-7 text-xs"
                    data-testid="new-row-notes"
                  />
                </TableCell>
                {!readOnly && (
                  <TableCell className="px-2 py-1.5">
                    <div className="flex gap-1">
                      <Button size="icon" className="h-6 w-6 bg-emerald-600 hover:bg-emerald-700" onClick={addTask} data-testid="confirm-add-task">
                        <Plus className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setShowNewRow(false)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Footer bar */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>
          {tasks.length} task{tasks.length !== 1 ? "s" : ""}
          {activeFilters.length > 0 &&
            ` (${table.getFilteredRowModel().rows.length} shown)`}
          {tasks.length > 0 &&
            ` | Total: ${tasks.reduce((s, t) => s + (t.estimated_duration_mins || 0), 0)} mins`}
        </span>
        {!readOnly && showAddForm && !showNewRow && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={() => setShowNewRow(true)}
            data-testid="add-task-row-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Task
          </Button>
        )}
      </div>
    </div>
  );
}
